<template>
  <SettingsModal v-if="triggerSettingsModalVisible" @close="triggerSettingsModalVisible = false" />

  <NavigationMoreSheet
    v-if="moreSheetVisible"
    :menu-items="moreItems"
    :show-settings="showSettings"
    :show-logout="showLogout"
    @close="moreSheetVisible = false"
    @settings="onSettings"
    @logout="onLogout"
  />

  <div
    id="infuse-navigation-bottom-bar"
    class="fixed -bottom-0 left-0 z-10 w-full bg-neutral-50 px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:hidden"
  >
    <div class="flex w-full flex-row justify-around gap-2">
      <router-link
        v-for="menuItem of pinnedItems"
        :key="menuItem.name"
        v-permission="{ ids: menuItem.permissionIds }"
        active-class="active"
        :to="menuItem.to"
        class="block"
      >
        <NavigationBottomBarItem>
          <template #icon>
            <component :is="getNavigationIcon(menuItem.icon)" class="h-5 w-5" />
          </template>
          <template #label>
            {{ te(menuItem.name) ? t(menuItem.name) : menuItem.name }}
          </template>
        </NavigationBottomBarItem>
      </router-link>

      <button type="button" class="block" :class="{ active: isMoreActive }" @click="moreSheetVisible = true">
        <NavigationBottomBarItem>
          <template #icon>
            <font-awesome-icon :icon="['fal', 'ellipsis']" class="h-5 w-5" />
          </template>
          <template #label>
            {{ t('nav.more') }}
          </template>
        </NavigationBottomBarItem>
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { User } from '@infuse/auth/services';
import { vPermission } from '@infuse/iam/permission/directives';
import { SettingsModal } from '@infuse/ui/components';
import { MenuItem } from '@infuse/ui/domain';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import NavigationBottomBarItem from './NavigationBottomBarItem.vue';
import NavigationMoreSheet from './NavigationMoreSheet.vue';
import { getNavigationIcon } from './NavigationIcons';

interface Props {
  menuItems: MenuItem[];
  user?: User;
  settings?: boolean;
  showSettings?: boolean;
  /**
   * Whether to render the logout item; an app with its own logout (e.g. in a topbar) hides it.
   */
  showLogout?: boolean;
}

const MAX_PINNED_ITEMS = 4;

const props = withDefaults(defineProps<Props>(), { showLogout: true });
const emit = defineEmits(['logout']);

const triggerSettingsModalVisible = ref(false);
const moreSheetVisible = ref(false);
const route = useRoute();
const { t, te } = useI18n();

// The bar holds at most 4 destinations plus "More"; everything else
// (including settings and logout) lives in the More sheet.
const pinnedItems = computed(() => props.menuItems.slice(0, MAX_PINNED_ITEMS));
const moreItems = computed(() => props.menuItems.slice(MAX_PINNED_ITEMS));

const isMoreActive = computed(() => moreItems.value.some((item) => route.path.startsWith(item.to)));

function onSettings() {
  moreSheetVisible.value = false;
  triggerSettingsModalVisible.value = true;
}

function onLogout() {
  moreSheetVisible.value = false;
  emit('logout');
}
</script>
